import { getSafeReturnTo } from "../../util/authUtils";

export const FLOATING_BRAND_CURRENT_PATH_KEY = "plany:floatingBrandCurrentPath";
export const FLOATING_BRAND_PREVIOUS_PATH_KEY = "plany:floatingBrandPreviousPath";

export function hasActiveTrialOverride(business) {
  if (!business?.exemptedPlanId) return false;
  const expiresAt = String(business?.exemptedPlanExpiresAt || "").trim();
  if (!expiresAt) return false;
  if (expiresAt.toLowerCase() === "neverexpires") return true;
  const parsed = new Date(expiresAt);
  return !Number.isNaN(parsed.getTime()) && parsed > new Date();
}

export function hasPaidBusinessPlanCode(business) {
  const plans = Array.isArray(business?.plans) ? business.plans : [];
  return plans.some((plan) => {
    const code = String(plan?.code || "").trim().toUpperCase();
    return Boolean(code) && code !== "FREE" && !code.endsWith("_FREE");
  });
}

export function shouldShowPublicBusinessBrand(business) {
  if (!business) return false;
  return hasActiveTrialOverride(business) || !hasPaidBusinessPlanCode(business);
}

export function syncFloatingBrandPathHistory(storage, currentPath) {
  if (!storage || !currentPath) return;

  try {
    const safeCurrentPath = getSafeReturnTo(currentPath);
    if (!safeCurrentPath) return;

    const lastCurrentPath = getSafeReturnTo(storage.getItem(FLOATING_BRAND_CURRENT_PATH_KEY));
    if (lastCurrentPath && lastCurrentPath !== safeCurrentPath) {
      storage.setItem(FLOATING_BRAND_PREVIOUS_PATH_KEY, lastCurrentPath);
    }

    storage.setItem(FLOATING_BRAND_CURRENT_PATH_KEY, safeCurrentPath);
  } catch {
    // Ignore storage failures so the brand never breaks navigation rendering.
  }
}

export function getStoredFloatingBrandPreviousPath(storage) {
  if (!storage) return null;
  try {
    return getSafeReturnTo(storage.getItem(FLOATING_BRAND_PREVIOUS_PATH_KEY));
  } catch {
    return null;
  }
}

export function getSafeSameOriginReferrerPath(referrer, origin) {
  if (!referrer || !origin) return null;

  try {
    const url = new URL(referrer);
    if (url.origin !== origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function resolveFloatingBrandNavigation({
  currentPath = "",
  fromState = null,
  storedPreviousPath = null,
  referrer = "",
  origin = "",
} = {}) {
  const safeFrom = typeof fromState === "string" ? getSafeReturnTo(fromState) : null;
  if (safeFrom && safeFrom !== currentPath) {
    return { mode: "path", to: safeFrom };
  }

  const safeStoredPreviousPath =
    typeof storedPreviousPath === "string" ? getSafeReturnTo(storedPreviousPath) : null;
  if (safeStoredPreviousPath && safeStoredPreviousPath !== currentPath) {
    return { mode: "path", to: safeStoredPreviousPath };
  }

  const safeReferrer = getSafeSameOriginReferrerPath(referrer, origin);
  if (safeReferrer && safeReferrer !== currentPath) {
    return { mode: "path", to: safeReferrer };
  }

  return { mode: "path", to: "/" };
}
